/* eslint-disable max-statements */
import React, { useState } from 'react'
import { Button, BodyText, PAYPAL_THEME } from '@paypalcorp/pp-react'
import serverData from 'ppbl/utils/server-data'
import { CleverContext } from './cleverContext'
import {
  AverageMonthlyGrossSales,
  Offer,
  ProductOfferConstraint,
} from '../../types'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import CleverOfferInput from './cleverOfferInput'
import CleverTermCard from './cleverTermCard'
import { santizeInputFields } from 'ppbl/utils/string-helper'
import './styles.css'
import PaymentDaySelector from './paymentDaySelector'
import InformationCard from './informationCard'
import CleverTierSection from './cleverTierSection'
import { useMutation } from '@apollo/client'
import { createOfferMutation } from 'ppbl/graphql/calculator/mutations'
import clientLogger from 'ppbl/utils/client-logger'
import CleverOfferCalculatorTable from './offerCalculaterTable'
import { isLoanAutomationQualified } from 'ppbl/utils/clever-utils'
import { trackClick } from 'ppbl/utils/fpti-helper'
import PromotionalAlert from 'ppbl/screens/cleverCalculator-page/promotionalAlert'
import {
  promotionalAlertConstants,
  promotionType,
} from 'ppbl/utils/cleverConstants'

const salesforceUrl = serverData.data.appConfig.application?.salesforceLoginURL
const { sysColorErrorMain } = PAYPAL_THEME

type Props = {
  opportunityId: string
  averageMonthlyGrossSales: AverageMonthlyGrossSales
  customOfferConstraints: Offer[]
  productOfferConstraints: ProductOfferConstraint[]
  applicationType: string
  application: any //TODO add type
  userId: string
}

const CleverOfferConfigurator: React.FC<Props> = ({
  averageMonthlyGrossSales,
  opportunityId,
  customOfferConstraints,
  productOfferConstraints,
  applicationType,
  application,
  userId,
}) => {
  const offerTermOptions = ['52']
  const {
    minAmount: { value: minAmount },
    maxAmount: { value: maxAmount },
  } = productOfferConstraints[0]

  let offerMinAmount = Number(minAmount)
  let offerMaxAmount = Number(maxAmount)
  const offerConstraints =
    application?.evaluation?.underwriting?.offerConstraints
  const defaultLoanAmount = Math.floor(offerConstraints
    ? Number(offerConstraints[0].maxLoanAmount.value)
    : offerMaxAmount)
  const { tags = null } = application

  const [loanTerm, setLoanTerm] = useState('52')
  const [weeklyPayment, setWeeklyPayment] = useState(0)
  const [loanFee, setLoanFee] = useState(0)
  const [loanPayOff, setLoanPayoff] = useState(0)
  const [annualPercentageRate, setAnnualPercentageRate] = useState(0)
  const [averageMonthlyCost, setAverageMonthlyCost] = useState(0)
  const [disbursementAmount, setDisbursementAmount] = useState('')
  const [termInDays, setTermInDays] = useState(0)
  const [loanAmount, updateLoanAmount] = useState(String(defaultLoanAmount))
  const [offerValues, updateOfferValues] = useState<any>({})
  const [continueDisabled, disableContinue] = useState(false)
  const [optionSelected, setOptionSelected] = useState('offer-option-1')
  const [paymentDay, setPaymentDay] = useState(
    application.weeklyPaymentDay || 'FRIDAY',
  )
  const [reloadTable, setReloadTable] = useState(false)
  const [snapError, setSnapError] = useState(false)
  const [isPromotionalAlert, setIsPromotionalAlert] = useState(false)
  const [selectedTier, setSelectedTier] = useState()
  const partnerIdentityAccessToken = serverData.cleverAccessToken

  const [createOffer, { loading: createOfferLoading }] = useMutation(
    createOfferMutation,
    {
      onError(createOfferError) {
        clientLogger.cleverError(
          'Error while calling GraphQl clever createOffer end point, redicting to error page',
          createOfferError?.message,
        )
        setSnapError(true)
      },
      onCompleted(createOfferData) {
        clientLogger.cleverInfo(
          `Offer created for opportunityId: ${opportunityId}, offerId: `,
          createOfferData?.credit?.businessCredit?.createdOffer?.offer?.id,
        )
        clientLogger.cleverInfo(
          `Offer created for amount: `,
          createOfferData?.credit?.businessCredit?.createdOffer?.offer
            ?.formattedAmount?.amount,
        )
        setReloadTable(true)
        setSnapError(false)
      },
    },
  )

  const getPayLoadToCreateOffer = () => {
    const term =
      offerValues?.termInWeeks !== undefined
        ? `P${offerValues?.termInWeeks}W`
        : null
    const proportionOfBusinessRevenue = offerValues?.maxProportionOfRevenue
    const weeklyInterestRate = offerValues?.weeklyInterestRate
    const offerAmount = String(offerValues.offerAmount)
    santizeInputFields(offerAmount)
    const fee = String(offerValues.financeCharges)

    let offerPayload = {
      offerEvaluationId: opportunityId,
      offer: {
        term,
        proportionOfBusinessRevenue,
        weeklyInterestRate,
        amount: { currencyCode: 'USD', value: offerAmount },
        fee: { currencyCode: 'USD', value: fee },
        weeklyPaymentDay: paymentDay,
        creatorUserId: userId,
      },
      partnerIdentityAccessToken,
    }

    return offerPayload
  }

  const handleCreateOffer = () => {
    const offerPayLoad = getPayLoadToCreateOffer()
    createOffer({
      variables: {
        offerInput: offerPayLoad,
      },
    })
  }

  const validateDate = (date) => {
    if (date && date !== '') {
      return new Date(date).toISOString().split('T')[0]
    }
    return null
  }

  const snapClickHandler = () => {
    const fptiData = {
      page: 'cleverCalculatorPage',
      component: 'cleverSnapButton',
      link: 'clever_snap_button',
    }
    trackClick(fptiData)
    if (application?.promotions?.length > 0) {
      const promotionalData = application?.promotions.find(
        (item) => item.type === promotionType.FEE_WAIVER,
      )
      const currentTier =
        selectedTier && String(selectedTier).toLowerCase().includes('tier')
          ? parseInt(String(selectedTier).replace(/[^0-9]/g, ''), 10)
          : 0

      const isPromotionActive =
        validateDate(promotionalData?.expiryDate) &&
        validateDate(promotionalData?.expiryDate) >=
          new Date().toISOString().split('T')[0]

      if (
        isPromotionActive &&
        promotionalData?.feeWaiver?.pricingTier &&
        currentTier !== promotionalData?.feeWaiver?.pricingTier
      ) {
        setIsPromotionalAlert(true)
      } else {
        handleCreateOffer()
      }
    }
  }

  const handlePromotionalAlert = (response: string) => {
    if (response === promotionalAlertConstants.confirm) {
      const fptiData = {
        page: 'cleverCalculatorPage',
        component: 'cleverPromotionalAlertConfirmButton',
        link: 'clever_promotional_alert_confirm_button',
      }
      trackClick(fptiData)
      handleCreateOffer()
    } else {
      const fptiData = {
        page: 'cleverCalculatorPage',
        component: 'cleverPromotionalAlertCancelButton',
        link: 'clever_promotional_alert_cancel_button',
      }
      trackClick(fptiData)
    }
    setIsPromotionalAlert(false)
  }

  const showAddBank = isLoanAutomationQualified(
    tags,
    offerValues.offerAmount,
    offerValues?.maxAutoQualifiedAmount,
  )

  const onClickAddBank = () => {
    const addBankUrl = `${salesforceUrl}/apex/bankaccountdetails?oppId=${opportunityId}`
    window.open(addBankUrl, '_blank')
  }

  const showPreviousLoanDetails =
    !!application?.business?.loanHistory?.previousLoans[0]

  const isRenewal = showPreviousLoanDetails && application?.refinance?.selected

  return (
    <CleverContext.Provider
      value={{
        loanAmount,
        loanTerm,
        weeklyPayment,
        annualPercentageRate,
        customOfferConstraints,
        averageMonthlyCost,
        disbursementAmount,
        termInDays,
        loanFee,
        loanPayOff,
        offerMaxAmount,
        offerMinAmount,
        offerValues,
        averageMonthlyGrossSales,
        reloadTable,
        setSelectedTier,
        setReloadTable,
        updateLoanAmount,
        setLoanTerm,
        setWeeklyPayment,
        setLoanFee,
        setLoanPayoff,
        continueDisabled,
        disableContinue,
        updateOfferValues,
        paymentDay,
        setPaymentDay,
        application,
        opportunityId,
      }}
    >
      <div className="main-container">
        <div className="sidebar">
          <InformationCard contentCardType="Opportunity Information" />
          <div className="clever-payment-day">
            <PaymentDaySelector />
          </div>
          {isRenewal && (
            <InformationCard contentCardType="Renewal Information" />
          )}
          {showPreviousLoanDetails && (
            <InformationCard contentCardType="Prior Loan Information" />
          )}
        </div>
        <div className="main-content">
          <a
            id="calculatorPage_opportunityLink"
            className="calculatorPage-title-link"
            href={`${salesforceUrl}/${opportunityId}`}
          >
            {application?.business?.legalBusinessName || ''}
          </a>
          <CleverOfferInput />
          <div className="termContainer">
            {offerTermOptions.map((el, index) => (
              <CleverTermCard
                initialTerm={el}
                optionSelected={optionSelected}
                setOptionSelected={setOptionSelected}
                index={index}
                key={index}
              />
            ))}
          </div>
          <div className="btn-container">
            <Button
              disabled={continueDisabled}
              className="button"
              btnState={createOfferLoading ? 'processing' : 'initial'}
              onClick={snapClickHandler}
            >
              {getLocalizedText(
                'cleverCalculator-page.cleverOfferConfigurator.takeSnapButtonText',
              )}
            </Button>
            {snapError && (
              <BodyText
                style={{ marginTop: '1.5rem', color: sysColorErrorMain }}
                strong
              >
                {getLocalizedText(
                  'cleverCalculator-page.cleverOfferConfigurator.takeSnapErrorText',
                )}
              </BodyText>
            )}
            {showAddBank && (
              <Button onClick={onClickAddBank} className="add-bank-btn">
                {getLocalizedText(
                  'cleverCalculator-page.cleverOfferConfigurator.addBankButtonText',
                )}
              </Button>
            )}
          </div>
          <CleverTierSection />
          <div className="offerCalculatorTable">
            <CleverOfferCalculatorTable opportunityId={opportunityId} />
          </div>
        </div>
      </div>
      {isPromotionalAlert && (
        <PromotionalAlert
          isPromotionalAlert={isPromotionalAlert}
          opportunityId={opportunityId}
          setIsPromotionalAlert={setIsPromotionalAlert}
          handlePromotionalAlert={handlePromotionalAlert}
        />
      )}
    </CleverContext.Provider>
  )
}

export default CleverOfferConfigurator
